import { Component, DestroyRef, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AuthService } from './shared/services/auth.service';
import { environment } from 'src/environments/environment';
import { Partner } from './core/models/partner.model';
import { PartnerService } from './core/services/partner.service';
import { ProfileResponse } from './core/models/profile.model';
import { NavigationEnd, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { filter, first } from 'rxjs';
import { Permissions } from './core/models/permissions.enum';
import { PermissionsService } from './shared/services/permissions.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalMapComponent } from './shared/components/global-map/global-map.component';
import { ModalErrorComponent } from './shared/components/modal-error/modal-error.component';
import { Profile } from './shared/common/enums';
import { GlobalMapGoogleMapsComponent } from './shared/components/global-map-v2/global-map-google-maps.component';
import { DesmobilizacaoMapComponent } from './shared/components/desmobilizacao/desmobilizacao-map.component';
import { Session } from 'src/app/shared/common/enums';
import { APP_VERSION, BUILD_TIME } from 'src/environments/version';
import { LayoutVisibilityService } from './shared/services/layout-visibility.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent {
    public title: string = 'telemetria';
    public showLogout: boolean = false;
    public userName: string = '';
    public environment = environment;
    public partners: Partner[] = [];
    public partnerId: string | number = '';
    public Permissions = Permissions;
    public Profile = Profile;
    public userPermissions: any = {};
    public userProfileId!: number;
    public loadingprofiles: boolean = false;
    public userIsDeveloper: boolean = false;
    public showHeader: boolean = true;
    public profileNotAllowed: boolean = false;

    constructor(
        private authService: AuthService,
        private partnerService: PartnerService,
        public router: Router,
        private msalService: MsalService,
        public permissionsService: PermissionsService,
        private modalService: NgbModal,
        private layoutVisibilityService: LayoutVisibilityService,
        private destroyRef: DestroyRef
    ) {
        authService.isLoggedInObs().subscribe((value) => {
            this.userName = authService.getUser()?.name || '';
            this.showLogout = value && this.userName ? true : false;

            if (value && !this.partners.length && authService.getToken() && this.userName) {
                this.loadingprofiles = true;
                this.getPartners();
                this.getUserPermissions();
            }
        });

        this.monitorRoutes();
        this.getHeaderVisibility();
        this.showLogVersion();
    }

    private getPartners() {
        this.partnerService.getpartners().subscribe((resp: Partner[]) => {
            this.partners = resp;
            this.hasSelectedPartner();

            this.loadingprofiles = false;
        });
    }

    private hasSelectedPartner() {
        const partner = this.partnerService.getPartnerId();
        if (partner) this.partnerId = partner;
    }

    public changePartner() {
        this.partnerService.setPartnerId(this.partnerId);
        this.reloadPage();
    }

    private reloadPage() {
        const currentUrl = this.router.url;

        this.router.navigateByUrl('/docs', { skipLocationChange: true }).then(() => {
            this.router.navigate([currentUrl]);
        });
    }

    private getUserPermissions() {
        const account = this.msalService.instance.getActiveAccount();

        if (account) {
            this.authService
                .getUserPermissions(account?.username)
                .pipe(first())
                .subscribe((resp: [{ chave: string }]) => {
                    if (resp.length) {
                        this.permissionsService.setUserPermissions(resp);
                        this.userPermissions = this.permissionsService.getUserPermissions();
                    } else {
                        this.permissionError();
                    }
                });

            this.authService
                .getUserProfile(account?.username)
                .pipe(first())
                .subscribe((resp: ProfileResponse) => {
                    this.permissionsService.setProfileId(resp.perfil.id);
                    this.userProfileId = this.permissionsService.getProfileId()!;
                    this.profileNotAllowed = [Profile.PRICING, Profile.LIVRE, Profile.IMPLANTATION].includes(this.userProfileId);
                    this.userIsDeveloper = resp.ehDesenvolvedor;
                });
        }
    }

    public openGlobalMapGoogleMaps() {
        this.modalService.open(GlobalMapGoogleMapsComponent, { fullscreen: true });
    }

    public openDesmobilizacao() {
        this.modalService.open(DesmobilizacaoMapComponent, { fullscreen: true });
    }

    public openGlobalMap() {
        this.modalService.open(GlobalMapComponent, { fullscreen: true });
    }

    private permissionError() {
        const modalRef = this.modalService.open(ModalErrorComponent);
        modalRef.componentInstance.message = 'Seu usuário não possui permissão de acesso.';
        modalRef.dismissed.subscribe((resul) => {
            this.authService.clearToken();
            this.router.navigateByUrl('/logout');
        });
    }

    private monitorRoutes(): void {
        const ignoredRoutes = ['/', '/logout'];

        this.router.events
        .pipe( filter(event => event instanceof NavigationEnd) )
        .subscribe((event: NavigationEnd) => {
            const url = event.urlAfterRedirects;
            if ( !ignoredRoutes.includes(url) ) {
                sessionStorage.setItem(Session.LAST_URL, url);
            }
        });
    }

    private showLogVersion() {
        console.info(
            `%cApp version: ${APP_VERSION} | Build: ${BUILD_TIME}`,
            'color: green; font-weight: bold;'
        );
    }

    private getHeaderVisibility() {
        this.layoutVisibilityService.getHeaderVisibility()
                                    .pipe(takeUntilDestroyed(this.destroyRef)).
                                    subscribe((visible: boolean) => {
            this.showHeader = visible;
        });
    }
}
